import React, { memo, useEffect, useState } from 'react';
import { Card, CardActionArea, Typography, Chip, Box, IconButton, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import moment from 'moment';
import { PapiroConsole } from 'src/utils/papiroConsole';

const useStyles = makeStyles((theme) => ({
  timelineItemWrapper: {
    position: 'relative',
    marginBottom: theme.spacing(10),
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1.5),
  },
  card: {
    width: '100%',
    maxWidth: '280px',  // Reduzido de 300px para caber melhor
    minWidth: '200px',  // Reduzido de 250px para telas menores
    borderRadius: '14px',
    border: '1px solid',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    },
  },
  cardContent: {
    padding: theme.spacing(2, 2.5),
  },
  headerBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  iconContainer: {
    width: 38,
    height: 38,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'transform 0.2s ease',
  },
  contentBox: {
    flex: 1,
    minWidth: 0,
  },
  labelsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.75),
  },
  typeChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
  },
  idContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  
  idBadge: {
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.3px',
    fontFamily: 'monospace',
    lineHeight: 1.6,
    display: 'inline-flex',
    alignItems: 'center',
  },
  idLabel: {
    fontSize: '0.72rem',
    color: '#78909C',
    fontWeight: 500,
    fontFamily: 'monospace',
  },
  openInNewButton: {
    padding: '3px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    opacity: 0.6,
    '&:hover': {
      opacity: 1,
      transform: 'scale(1.1)',
    },
  },
  title: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#37474F',
    lineHeight: 1.45,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
    letterSpacing: '-0.01em',
  },
  footerBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: '1px solid rgba(0,0,0,0.05)',
  },
  timeBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    flexShrink: 0,
  },
  statusChip: {
    height: '22px !important',
    fontSize: '0.68rem !important',
    fontWeight: '600 !important',
    borderRadius: '6px !important',
    letterSpacing: '0.2px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
  },
  centralIndicator: {
    position: 'absolute',
    left: '50%',
    top: '24px',
    transform: 'translateX(-50%)',
    width: 12,  // Reduzido de 14 para 12
    height: 12, // Reduzido de 14 para 12
    borderRadius: '50%',
    border: '2px solid #FFFFFF', // Reduzido de 3px para 2px
    zIndex: 2,
    transition: 'all 0.25s ease',
  },
  // Connector line from card to central indicator
  connectorLine: {
    position: 'absolute',
    top: '29px', // Ajustado para alinhar com o novo tamanho do indicador
    height: '2px',
    zIndex: 1,
  },
}));

// Detecta o tema atual baseado nas variáveis CSS
const detectTheme = () => {
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--mexx-green-primary')
    .trim();
  
  const isBlueTheme = primaryColor.includes('2979ff') || primaryColor.includes('#2979ff');
  return isBlueTheme ? 'blue' : 'green';
};

// Paleta de cores refinada baseada no tema
const getThemeColors = (theme) => {
  if (theme === 'blue') {
    return {
      called: {
        primary: '#1E88E5',
        light: '#E3F2FD',
        dark: '#1565C0',
        border: '#BBDEFB',
        shadow: 'rgba(30, 136, 229, 0.15)',
      },
      chat: {
        primary: '#00897B',
        light: '#E0F2F1',
        dark: '#00695C',
        border: '#B2DFDB',
        shadow: 'rgba(0, 137, 123, 0.15)',
      },
    };
  } else {
    return {
      called: {
        primary: '#558B2F',
        light: '#F1F8E9',
        dark: '#33691E',
        border: '#DCEDC8',
        shadow: 'rgba(85, 139, 47, 0.15)',
      },
      chat: {
        primary: '#7CB342',
        light: '#F9FBE7',
        dark: '#689F38',
        border: '#E6EE9C',
        shadow: 'rgba(124, 179, 66, 0.15)',
      },
    };
  }
};

// Configuração de tipo baseada no itemType e tema
const getTypeConfig = (itemType, themeColors) => {
  const isCalled = itemType === 'called' || itemType === 'Called';
  const colors = isCalled ? themeColors.called : themeColors.chat;
  
  return {
    ...colors,
    icon: isCalled ? AssignmentIcon : ChatIcon,
    label: isCalled ? 'Chamado' : 'Chat'
  };
};

const getItemType = (item) => {
  PapiroConsole.log("item.type getItemType")
  PapiroConsole.log(item.type)
  if (item.type) {
    return item.type.toLowerCase();
  }
  if (item.calledId || item.called) {
    return 'called';
  }
  if (item.chatId || item.chat) {
    return 'chat';
  }
  return 'called';
};

const handleClickGoToCalled = async (ticketid, itemType = 'called', newTab = true) => {
  let urlRedirect;
  
  if (itemType === 'chat' || itemType === 'Chat') {
    urlRedirect = "/pt/omnichannel-observe/" + ticketid;
  } else {
    urlRedirect = "/pt/called-accompaniment/" + ticketid;
  }
  
  setTimeout(() => {
    if(newTab) {
      window.open(urlRedirect, "_blank");
    } else {
      window.open(urlRedirect);
    }
  }, 100);
};

const HistoryItem = memo(({ item, index, onClick, onOpenInNew }) => {
  const classes = useStyles();
  const [currentTheme, setCurrentTheme] = useState('green');
  
  useEffect(() => {
    const detected = detectTheme();
    setCurrentTheme(detected);
    
    const observer = new MutationObserver(() => {
      const newTheme = detectTheme();
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
      }
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });
    
    return () => observer.disconnect();
  }, [currentTheme]);
  
  if (!item) {
    console.warn('HistoryItem: item is undefined');
    return null;
  }
  
  const isLeft = index % 2 === 0;
  const itemType = getItemType(item);
  const themeColors = getThemeColors(currentTheme);
  const typeConfig = getTypeConfig(itemType, themeColors);
  const Icon = typeConfig.icon;

  const timeStr = item.createdat 
    ? moment(item.createdat).format('HH:mm')
    : '--:--';

  const itemId = item.id || item.calledId || item.chatId || '---';

  const handleOpenInNew = (e) => {
    e.stopPropagation();
    handleClickGoToCalled(itemId, itemType, true);
    
  };

  return (
    <Box
      className={classes.timelineItemWrapper}
      sx={{
        justifyContent: isLeft ? 'flex-start' : 'flex-end',
        // Ajuste responsivo: usa calc() para manter distância proporcional da linha central
        paddingRight: isLeft ? 'calc(50% + 16px)' : 0,
        paddingLeft: isLeft ? 0 : 'calc(50% + 16px)',
      }}
    >
      {/* Connector line */}
      <Box
  className={classes.connectorLine}
  sx={{
    borderTop: `2px dashed ${typeConfig.border}`,
    backgroundColor: 'transparent',
    left: isLeft ? 'auto' : '50%',
    right: isLeft ? '50%' : 'auto',
    // Largura fixa pequena para a linha de conexão
    width: '14px',
    opacity: 0.8,
  }}
/>

      <Card
        className={classes.card}
        sx={{
          borderColor: typeConfig.border,
          '&:hover': {
            borderColor: typeConfig.primary,
            boxShadow: `0 8px 28px ${typeConfig.shadow}`,
            '& .indicator': {
              boxShadow: `0 0 0 4px ${typeConfig.light}`,
            },
          },
        }}
      >
        <CardActionArea 
          onClick={() => onClick && onClick(item)}
          sx={{
            '&:focus': {
              outline: `2px solid ${typeConfig.primary}`,
              outlineOffset: '2px',
            },
          }}
        >
          <Box className={classes.cardContent}>
            
            <Box className={classes.headerBox}>
              {/* Ícone com background */}
              <Box
                className={classes.iconContainer}
                sx={{
                  backgroundColor: typeConfig.light,
                }}
              >
                <Icon sx={{ fontSize: 20, color: typeConfig.primary }} />
              </Box>

              <Box className={classes.contentBox}>
                <Box className={classes.labelsRow}>
                  {/* Type chip */}
                  <Box
                    className={classes.typeChip}
                    sx={{ 
                      backgroundColor: typeConfig.light,
                      color: typeConfig.dark,
                    }}
                  >
                    {typeConfig.label}
                  </Box>
                  
                  {/* ID com botão de abrir em nova aba */}
                  <Box className={classes.idContainer}>
                        <Box
                          className={classes.idBadge}
                          sx={{
                            backgroundColor: typeConfig.light,
                            color: typeConfig.dark,
                            border: `1px solid ${typeConfig.border}`,
                          }}
                        >
                          #{itemId}
                        </Box>

                        <Tooltip title="Abrir em nova aba" arrow placement="top">
  <IconButton
    size="small"
    onClick={handleOpenInNew}
    sx={{
      padding: '4px',
      minWidth: 'unset',
      borderRadius: '6px',

      // 👇 cor fixa (a mesma que você usava no hover)
      color: typeConfig.dark,

      backgroundColor: typeConfig.border,
      opacity: 1,
      transition: 'all 0.2s ease',

      '&:hover': {
        backgroundColor: typeConfig.border,
        transform: 'scale(1.05)',
      },

      '&:focus-visible': {
        outline: `2px solid ${typeConfig.primary}`,
        outlineOffset: '2px',
      },
    }}
  >
    <OpenInNewIcon fontSize="inherit" />
  </IconButton>
</Tooltip>

                      </Box>
                </Box>

                <Typography className={classes.title}>
                  {item.title || item.name || `Atendimento #${itemId}`}
                </Typography>
              </Box>
            </Box>

            <Box className={classes.footerBox}>
              <Box className={classes.timeBox}>
                <ScheduleIcon sx={{ fontSize: 14, color: '#90A4AE' }} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    color: '#78909C',
                    fontWeight: 500,
                  }}
                >
                  {timeStr}
                </Typography>
              </Box>

              {item.statusname && (
                <Chip
                  label={item.statusname}
                  size="small"
                  className={classes.statusChip}
                  sx={{ 
                    bgcolor: item.statuscolor || typeConfig.primary,
                    color: '#ffffff',
                  }}
                />
              )}
            </Box>
          </Box>
        </CardActionArea>
      </Card>

      {/* Central indicator */}
      <Box
        className={`${classes.centralIndicator} indicator`}
        sx={{
          bgcolor: typeConfig.primary,
          boxShadow: `0 0 0 3px ${typeConfig.light}, 0 2px 8px ${typeConfig.shadow}`,
        }}
      />
    </Box>
  );
});

export default HistoryItem;